import { useState } from 'react'

import type { LANG } from '@eshop/type'
import type Rental from '@eshop/type/rental'

import i18n from './i18n'
import css from './index.module.css'

import { Input } from '#/global/component/Form/Input'

import { addToCart as addToCartDataLayer } from '#/global/helper/DataLayer/modalCrossSelling'
import { linkToProductRental } from '#/global/helper/LinkTo'
import { ProductHelper } from '#/global/helper/Product/ProductHelper'

import type { AddToCartRentalAfter, AddToCartRentalTypeOfRental } from '#/global/types'

interface Props {
    lang: LANG
    product: Rental.Product<'detail'>
    type: 'long' | 'slide'
}

interface PropsWithDate {
    date: {
        from: Date
        to: Date
    }
    hasGuarantee: boolean
    lang: LANG
    product: Rental.Product<'detail'>
    type: 'compact'
    typeOfRental: AddToCartRentalTypeOfRental
}

export const AddToCartRental = (props: Props | PropsWithDate) => {
    const { type } = props
    if (type === 'compact') {
        return <Compact {...props} />
    }
    if (type === 'long') {
        return <Long {...props} />
    }
    if (type === 'slide') {
        return <Slide {...props} />
    }
}

const Slide = ({ lang, product }: Props) => {
    const t = i18n[lang]
    return (
        <div className={`r-add-to-cart-rental-slide ${css.container}`}>
            <a className={`dek-button dek-button-green dek-button-small ${css.button}`} href={linkToProductRental(product.code, product.detail.slug)} role="button">
                {t.toDetail}
            </a>
        </div>
    )
}

const Long = ({ lang, product }: Props) => {
    const t = i18n[lang]
    return (
        <div className={`r-add-to-cart-rental-long ${css.container} ${css.topDivider}`}>
            <a className={`dek-button dek-button-green ${css.button}`} href={linkToProductRental(product.code, product.detail.slug)} role="button">
                {t.toDetail}
            </a>
        </div>
    )
}

const Compact = ({ date, hasGuarantee, lang, product, typeOfRental }: PropsWithDate) => {
    const { formatPrice } = ProductHelper

    const t = i18n[lang]

    const [quantity, setQuantity] = useState(1)
    const [price, setPrice] = useState(product.detail.priceVatOrigin)
    const [isAlreadyInCart, setIsAlreadyInCart] = useState(false)
    const [isAddingInProcess, setIsAddingInProcess] = useState(false)

    const handleChange = (q: number) => {
        setQuantity(q)
        setPrice(q * product.detail.priceVatOrigin)
    }

    const addToCart = () => {
        setIsAddingInProcess(true)
        window.addEventListener(`ES_ADD_TO_CART_RENTAL_AFTER`, onAfterAddToCart)
        ProductHelper.addToCartRental({
            unionCode: product.code,
            quantity,
            date,
            hasGuarantee,
            typeOfRental,
            eventInvoker: 'compact',
        })
    }

    const onAfterAddToCart = (event: CustomEvent<AddToCartRentalAfter>) => {
        if (product.code !== event.detail.unionCode) {
            return
        }
        setIsAddingInProcess(false)
        setIsAlreadyInCart(event.detail.isSuccess)
        event.detail.isSuccess && addToCartDataLayer(product.code)
        window.removeEventListener(`ES_ADD_TO_CART_RENTAL_AFTER`, onAfterAddToCart)
    }

    return (
        <div className={`r-add-to-cart-rental-compact ${css.compact}`}>
            <div className={css.price}>
                <strong>{formatPrice(price, product.detail.currencyCode)}</strong>
                <div>
                    {t.unit(product.detail.unitPrice)}
                    {' '}
                    {t.withDPH}
                </div>
            </div>
            <div className={css.addToCart}>
                {isAlreadyInCart
                    ? (
                            <div className={css.alreadyInCart}>
                                <img src="https://cdn1.idek.cz/file/check-7c33cfe0.svg" width={16} height={16} />
                                <span>{t.addedToCart}</span>
                            </div>
                        )
                    : (
                            <>
                                <div className={css.input}>
                                    <Input
                                        defaultValue={quantity}
                                        disabled={isAddingInProcess}
                                        min={1}
                                        onChange={handleChange}
                                        type="number"
                                    />
                                    {product.detail.unitQuantity}
                                </div>
                                <button
                                    className="dek-button-green"
                                    disabled={isAddingInProcess}
                                    onClick={addToCart}
                                    role="button"
                                    type="button">
                                    {isAddingInProcess && <span className={css.loading} />}
                                    {t.addToCart}
                                </button>
                            </>
                        )}
            </div>
        </div>
    )
}
